// ✅ src/pages/TarMixing/ProductionListing.jsx
// Updated with safe localStorage handling + common headers

import React, { useState, useContext, useMemo } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProductionLayoutWrapper from "../../components/ProductionLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaPlus,
  FaChevronDown,
  FaChevronUp,
  FaTrash,
  FaEdit,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const ProductionListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // ✅ Safe localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchid = useMemo(() => toInt(branchIdLS, 8), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim(),
    [branchTypeLS]
  );
  const userid = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchid,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userid,
    }),
    [finYearId, finYear, branchid, branchType, userid]
  );

  const [showFilters, setShowFilters] = useState(true);

  const [formData, setFormData] = useState({
    fromDate: "01/01/2021",
    toDate: "11/12/2025",
  });

  const [productionData, setProductionData] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ✅ API returns: productionid
  const getRowId = (row) => {
    const id = Number(row?.productionid);
    return Number.isFinite(id) && id > 0 ? id : null;
  };

  const validRowIds = useMemo(() => {
    return (productionData || []).map(getRowId).filter((x) => x != null);
  }, [productionData]);

  // -------------------------
  // Checkbox selection
  // -------------------------
  const toggleRow = (id) => {
    if (id == null) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const allSelected =
    validRowIds.length > 0 && selectedIds.length === validRowIds.length;

  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(validRowIds);
  };

  // -------------------------
  // Search
  // -------------------------
  const handleSearch = async () => {
    if (!branchid) return toast.error("BranchId missing!");
    if (!formData.fromDate || !formData.toDate)
      return toast.warn("From Date and To Date required");

    try {
      setLoading(true);
      setHasSearched(true);

      const res = await axios.get(`${BASE_URL}/api/productionListing/search`, {
        params: {
          branchid,
          userid,
          fromDate: formData.fromDate,
          toDate: formData.toDate,
        },
        headers: commonHeaders,
      });

      const rows = res.data?.data || [];
      console.log("res", rows);
      setProductionData(rows);
      setSelectedIds([]);

      if (rows.length === 0) toast.info("No Records found");
    } catch (err) {
      console.error("Search error:", err);
      toast.error(err?.response?.data?.message || "Search failed");
      setProductionData([]);
      setSelectedIds([]);
    } finally {
      setLoading(false);
    }
  };

  // -------------------------
  // Add new
  // -------------------------
  const handleAddNew = () => {
    navigate("/TarMixing/Tar_Production_Entry");
  };

  // -------------------------
  // ✅ Update functionality
  // -------------------------
  const handleUpdate = async () => {
    if (selectedIds.length === 0)
      return toast.warn("Please select one record to update.");

    if (selectedIds.length > 1)
      return toast.warn("Please select only one record for update.");

    const id = selectedIds[0];
    const rowData = productionData.find(
      (r) => Number(r.productionid) === Number(id)
    );

    if (!rowData) {
      return toast.error("Selected record not found!");
    }

    try {
      setLoading(true);

      navigate("/TarMixing/Tar_Production_Entry", {
        state: {
          editMode: true,
          id,
          rowData,
        },
      });
    } catch (err) {
      console.error("Update getById error:", err);
      toast.error(
        err?.response?.data?.message || "Failed to load record for update"
      );
    } finally {
      setLoading(false);
    }
  };

  // -------------------------
  // Delete selected (multi)
  // -------------------------
  const handleDelete = async () => {
    if (selectedIds.length === 0)
      return toast.warn("Select at least one row to delete.");

    const ok = window.confirm(
      `Are you sure you want to delete ${selectedIds.length} record(s)?`
    );
    if (!ok) return;

    try {
      setLoading(true);

      const res = await axios.delete(
        `${BASE_URL}/api/productionListing/deleteSelected`,
        {
          params: { branchid, userid },
          data: { ids: selectedIds },
          headers: commonHeaders,
        }
      );

      if (res.data?.success === false) {
        toast.error(res.data?.message || "Delete failed");
        return;
      }

      toast.success(res.data?.message || "Deleted successfully!");

      setProductionData((prev) =>
        prev.filter((r) => !selectedIds.includes(getRowId(r)))
      );
      setSelectedIds([]);
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err?.response?.data?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProductionLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PRODUCTION LISTING</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header row */}
          <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Production Listing
            </h1>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus /> Add New
              </button>

              <button
                onClick={handleUpdate}
                disabled={loading}
                className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  loading ? "opacity-60 cursor-not-allowed" : ""
                } bg-blue-600 hover:bg-blue-700 text-white`}
              >
                <FaEdit /> {loading ? "Loading..." : "Update"}
              </button>

              <button
                onClick={handleDelete}
                disabled={loading}
                className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  loading ? "opacity-60 cursor-not-allowed" : ""
                } bg-red-600 hover:bg-red-700 text-white`}
              >
                <FaTrash /> Delete
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`p-6 rounded-xl border mb-6 ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    From Date
                  </label>
                  <input
                    type="text"
                    value={formData.fromDate}
                    onChange={(e) => handleInputChange("fromDate", e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    To Date
                  </label>
                  <input
                    type="text"
                    value={formData.toDate}
                    onChange={(e) => handleInputChange("toDate", e.target.value)}
                    placeholder="DD/MM/YYYY"
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>

              <div className="flex justify-center items-center gap-4 mt-8">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                    loading ? "opacity-60 cursor-not-allowed" : ""
                  } ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaSearch /> {loading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {loading && productionData.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <span className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</span>
              </div>
            ) : productionData.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <span className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasSearched ? "No Records found" : "Please search to view records"}
                </span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className={`min-w-full text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">
                        <div className="flex items-center gap-2">
                          <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                          Select
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">S.No</th>
                      <th className="px-4 py-3 text-left font-semibold">Date</th>
                      <th className="px-4 py-3 text-left font-semibold">Item Name</th>
                      <th className="px-4 py-3 text-right font-semibold">Quantity</th>
                    </tr>
                  </thead>

                  <tbody className={`${isDark ? "bg-gray-900" : "bg-white"}`}>
                    {productionData.map((row, idx) => {
                      const id = getRowId(row);
                      const checked = selectedIds.includes(id);

                      return (
                        <tr
                          key={id || idx}
                          className={`border-t ${
                            isDark ? "border-gray-800 hover:bg-gray-800/50" : "border-gray-100 hover:bg-gray-50"
                          }`}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleRow(id)}
                            />
                          </td>
                          <td className="px-4 py-3">{idx + 1}</td>
                          <td className="px-4 py-3">
                            {row.fld_ProductionDate
                              ? new Date(row.fld_ProductionDate).toLocaleDateString("en-GB")
                              : "-"}
                          </td>
                          <td className="px-4 py-3">{row.ProductionName || "-"}</td>
                          <td className="px-4 py-3 text-right">{row.Quantity ?? 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className={`px-4 py-3 text-sm ${isDark ? "bg-gray-800 text-gray-300" : "bg-gray-50 text-gray-700"}`}>
                  Selected: <span className="font-semibold">{selectedIds.length}</span>
                </div>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProductionLayoutWrapper>
  );
};

export default ProductionListing;